import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Grid,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import axiosInstance from "../utils/axiosInstance";
import ServiceProviderForm from "../components/admin/ServiceProviderForm";

const Services = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);

  // ✅ Fetch providers from backend
  const fetchProviders = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/services");

      // 🔑 Backend returns { success: true, data: [...] }
      const mappedProviders = res.data.data.map((provider) => ({
        ...provider,
        verified: Boolean(provider.verified),
        category: provider.category || { id: null, name: "Uncategorized" },
        location: provider.location || "N/A",
        service: provider.service || "N/A",
      }));

      setProviders(mappedProviders);
    } catch (err) {
      console.error("Error fetching providers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setOpenForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this provider?")) return;
    try {
      await axiosInstance.delete(`/services/${id}`);
      fetchProviders();
    } catch (err) {
      console.error("Error deleting provider:", err);
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingProvider(null);
    fetchProviders();
  };

  const getCategoryName = (category) => category?.name || "Uncategorized";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (providers.length === 0) {
    return (
      <Typography variant="h6" align="center" sx={{ mt: 10 }}>
        No service providers found.
      </Typography>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Service Providers
      </Typography>
      <Button
        variant="contained"
        onClick={() => setOpenForm(true)}
        sx={{ mb: 3 }}
      >
        Add Provider
      </Button>

      <Grid container spacing={3}>
        {providers.map((p) => (
          <Grid item xs={12} sm={6} md={4} key={p.id}>
            <Card
              sx={{
                maxWidth: 350,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "scale(1.03)",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                },
              }}
            >
              {p.image_url && (
                <CardMedia
                  component="img"
                  image={p.image_url}
                  alt={p.name}
                  sx={{ width: "100%", height: 250, objectFit: "cover" }}
                />
              )}
              <CardContent
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{ fontWeight: "600", mb: 0.5 }}
                    title={p.name}
                  >
                    {p.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={p.service}
                    sx={{ mb: 1 }}
                  >
                    🛠️ {p.service}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 0.3 }}>
                    📞 {p.phone}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.3 }}>
                    📍 {p.location}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.3 }}>
                    🕒 Hours: {p.working_hours || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.3 }}>
                    📅 Days: {p.working_days || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.3 }}>
                    💼 Category: {getCategoryName(p.category)}
                  </Typography>

                  <Box
                    sx={{
                      mt: 1,
                      maxHeight: 90,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: "vertical",
                      color: "text.secondary",
                    }}
                    title={p.extra_info || "N/A"}
                  >
                    <Typography variant="body2" component="span">
                      ℹ️ Extra Info: {p.extra_info || "N/A"}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontWeight: "600" }}
                    color={p.verified ? "success.main" : "error.main"}
                  >
                    {p.verified ? "✅ Verified" : "❌ Not Verified"}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <IconButton
                    onClick={() => handleEdit(p)}
                    aria-label="edit provider"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(p.id)}
                    aria-label="delete provider"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {openForm && (
        <ServiceProviderForm
          existingData={editingProvider}
          onClose={handleCloseForm}
        />
      )}
    </Box>
  );
};

export default Services;
